import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Button } from '@components/Common';
import { IDialogComponent } from 'core/interfaces';
import { Timer } from '@components/TimerEvent';
import { CurrencyText } from '@components/currency';
import { getExteriorByFloat } from 'core/utils/colors.utils';
import { useSalesElement } from '../../../../hooks';
import { IMarketplaceElement } from '../../../../interfaces';
import { OrderStatusEnum } from '../../../../configs';
import { isFinishStatus } from '../../../../utils/status.utils';
import { SteamStatus } from '@components/steam-status';
import { URLS } from 'services/constant-urls';
import { Container, Skeleton } from './seller-trade-dialog.styles';
import { skinStickerImage } from 'core/utils/skinStickerImage.utils';
import { skinItemImage } from 'core/utils/skinItemImage.utils';

interface IDialogData {
  id: number;
  immediate: boolean;
}

interface ISellerTradeDialog extends IDialogComponent<IDialogData> {}

export const SellerTradeDialog: FC<ISellerTradeDialog> = ({
  handleCloseModal,
  data: { id, immediate },
}) => {
  const { item } = useSalesElement<IMarketplaceElement>(id.toString());
  const { t } = useTranslation('marketplace');

  const cn = useMemo<string>(() => {
    if (isFinishStatus(item.status) || !!item.order?.fastOfferError) return 'disabled';
    return '';
  }, [item]);

  return (
    <Container immediate={immediate} className={item.status}>
      <Button className="link-light close-dialog" onClick={handleCloseModal}>
        {t('close')}
      </Button>
      <div className="header-dialog">
        {[OrderStatusEnum.WAIT_FOR_TRADE, OrderStatusEnum.WAIT_FOR_TRADE_CONFIRM].includes(
          item.status
        ) ? (
          <>
            <SteamStatus />
            <span>{t('Sending the offer')}</span>
            <span>
              <Timer finishEvent={item?.nextStatusAt} format="mm:ss" />
            </span>
          </>
        ) : (
          <span>{t('Trade history')}</span>
        )}
      </div>
      {item.order?.buyerSteamName ? (
        <div className="profile">
          <div className="profile-thumbnails">
            <img src={`${URLS.urlAvatars}${item.order?.buyerSteamAvatar}.jpg`} alt="" />
          </div>
          <span className="profile-nickname">{item.order?.buyerSteamName}</span>
          <span className="profile-level">
            {item.order?.buyerSteamLevel} {t('seller-trade-offer.Level')}
          </span>
          <span className="profile-registered">{t('seller-trade-offer.Registration date')}</span>
          <span className="profile-date">
            {dayjs(item.order?.buyerSteamCreatedAt).format('DD:MM:YYYY')}
          </span>
        </div>
      ) : (
        <Skeleton className="skeleton" />
      )}
      <div className="details">
        <div className="details-wrapper">
          <div className="lot">
            <div className="thumbnails">
              <img src={skinItemImage(item.items?.[0]?.baseItem.icon)} alt="" />
            </div>
            <div className="stickers">
              {item.items?.[0]?.stickers?.map(({ icon }: { icon: string }, i: number) => (
                <img key={i} src={skinStickerImage(icon)} alt="" />
              ))}
            </div>
            <div className="data">
              <span className="exterior">
                {t(`exterior-short.${(item.items?.[0]?.baseItem.exterior ?? '').toLowerCase()}`)}
              </span>
              <span className={`float ${getExteriorByFloat(item.items?.[0]?.float)}`}>
                {!!item.items?.[0]?.float && item.items?.[0]?.float?.toFixed(4)}
              </span>
              <span className="name">{item.items?.[0]?.baseItem.name}</span>
              <span className="price">
                <CurrencyText value={item.price} />
              </span>
            </div>
          </div>
          <div className="lot-status">
            {[OrderStatusEnum.AUCTION_FINISHED, OrderStatusEnum.COMPLETED].includes(item.status) ? (
              <i className="icon-marketplace-complete" />
            ) : (
              <i className="icon-marketplace-delete" />
            )}
            <span className="lot-status-type">
              {t('statuses.seller', { context: item.status })}
            </span>
            <span className="lot-status-data">{dayjs(item.statusAt).format('DD:MM:YYYY')}</span>
            <svg
              width="100%"
              height="8"
              viewBox="0 0 344 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 4C8 6.20914 6.20914 8 4 8C1.79086 8 0 6.20914 0 4C0 1.79086 1.79086 0 4 0C6.20914 0 8 1.79086 8 4ZM104 4C104 6.20914 102.209 8 100 8C97.7909 8 96 6.20914 96 4C96 1.79086 97.7909 0 100 0C102.209 0 104 1.79086 104 4ZM220 8C222.209 8 224 6.20914 224 4C224 1.79086 222.209 0 220 0C217.791 0 216 1.79086 216 4C216 6.20914 217.791 8 220 8ZM56 4C56 6.20914 54.2091 8 52 8C49.7909 8 48 6.20914 48 4C48 1.79086 49.7909 0 52 0C54.2091 0 56 1.79086 56 4ZM148 8C150.209 8 152 6.20914 152 4C152 1.79086 150.209 0 148 0C145.791 0 144 1.79086 144 4C144 6.20914 145.791 8 148 8ZM272 4C272 6.20914 270.209 8 268 8C265.791 8 264 6.20914 264 4C264 1.79086 265.791 0 268 0C270.209 0 272 1.79086 272 4ZM328 8C330.209 8 332 6.20914 332 4C332 1.79086 330.209 0 328 0C325.791 0 324 1.79086 324 4C324 6.20914 325.791 8 328 8ZM32 4C32 6.20914 30.2091 8 28 8C25.7909 8 24 6.20914 24 4C24 1.79086 25.7909 0 28 0C30.2091 0 32 1.79086 32 4ZM124 8C126.209 8 128 6.20914 128 4C128 1.79086 126.209 0 124 0C121.791 0 120 1.79086 120 4C120 6.20914 121.791 8 124 8ZM248 4C248 6.20914 246.209 8 244 8C241.791 8 240 6.20914 240 4C240 1.79086 241.791 0 244 0C246.209 0 248 1.79086 248 4ZM76 8C78.2091 8 80 6.20914 80 4C80 1.79086 78.2091 0 76 0C73.7909 0 72 1.79086 72 4C72 6.20914 73.7909 8 76 8ZM200 4C200 6.20914 198.209 8 196 8C193.791 8 192 6.20914 192 4C192 1.79086 193.791 0 196 0C198.209 0 200 1.79086 200 4ZM172 8C174.209 8 176 6.20914 176 4C176 1.79086 174.209 0 172 0C169.791 0 168 1.79086 168 4C168 6.20914 169.791 8 172 8ZM296 4C296 6.20914 294.209 8 292 8C289.791 8 288 6.20914 288 4C288 1.79086 289.791 0 292 0C294.209 0 296 1.79086 296 4ZM16 8C18.2091 8 20 6.20914 20 4C20 1.79086 18.2091 0 16 0C13.7909 0 12 1.79086 12 4C12 6.20914 13.7909 8 16 8ZM116 4C116 6.20914 114.209 8 112 8C109.791 8 108 6.20914 108 4C108 1.79086 109.791 0 112 0C114.209 0 116 1.79086 116 4ZM232 8C234.209 8 236 6.20914 236 4C236 1.79086 234.209 0 232 0C229.791 0 228 1.79086 228 4C228 6.20914 229.791 8 232 8ZM68 4C68 6.20914 66.2091 8 64 8C61.7909 8 60 6.20914 60 4C60 1.79086 61.7909 0 64 0C66.2091 0 68 1.79086 68 4ZM160 8C162.209 8 164 6.20914 164 4C164 1.79086 162.209 0 160 0C157.791 0 156 1.79086 156 4C156 6.20914 157.791 8 160 8ZM284 4C284 6.20914 282.209 8 280 8C277.791 8 276 6.20914 276 4C276 1.79086 277.791 0 280 0C282.209 0 284 1.79086 284 4ZM340 8C342.209 8 344 6.20914 344 4C344 1.79086 342.209 0 340 0C337.791 0 336 1.79086 336 4C336 6.20914 337.791 8 340 8ZM44 4C44 6.20914 42.2091 8 40 8C37.7909 8 36 6.20914 36 4C36 1.79086 37.7909 0 40 0C42.2091 0 44 1.79086 44 4ZM136 8C138.209 8 140 6.20914 140 4C140 1.79086 138.209 0 136 0C133.791 0 132 1.79086 132 4C132 6.20914 133.791 8 136 8ZM260 4C260 6.20914 258.209 8 256 8C253.791 8 252 6.20914 252 4C252 1.79086 253.791 0 256 0C258.209 0 260 1.79086 260 4ZM316 8C318.209 8 320 6.20914 320 4C320 1.79086 318.209 0 316 0C313.791 0 312 1.79086 312 4C312 6.20914 313.791 8 316 8ZM92 4C92 6.20914 90.2091 8 88 8C85.7909 8 84 6.20914 84 4C84 1.79086 85.7909 0 88 0C90.2091 0 92 1.79086 92 4ZM208 8C210.209 8 212 6.20914 212 4C212 1.79086 210.209 0 208 0C205.791 0 204 1.79086 204 4C204 6.20914 205.791 8 208 8ZM188 4C188 6.20914 186.209 8 184 8C181.791 8 180 6.20914 180 4C180 1.79086 181.791 0 184 0C186.209 0 188 1.79086 188 4ZM304 8C306.209 8 308 6.20914 308 4C308 1.79086 306.209 0 304 0C301.791 0 300 1.79086 300 4C300 6.20914 301.791 8 304 8Z"
                fill="#182328"
              />
            </svg>
          </div>
        </div>
        <div className="warning-message">
          <i className="icon-warn" />
          <p>{t('seller-trade-offer.beware')}</p>
          <p>{t('seller-trade-offer.describe')}</p>
        </div>
      </div>
      <div className="controllers">
        {item.status === OrderStatusEnum.WAIT_FOR_TRADE_CONFIRM ? (
          <Button
            className={`primary with-progress ${cn} disabled`}
            duration={+(Date.parse(item?.nextStatusAt) - Date.now()).toFixed(0)}
            current={dayjs(item?.statusAt).diff(Date.now())}
            finished={dayjs(item?.statusAt).diff(item?.nextStatusAt)}
            style={{ pointerEvents: 'none' }}
          >
            <i className="icon-steam-provider" />
            <span className="multi-line">
              <span>{t('Waiting for confirm')}</span>
              <span>
                {t('Trade timeout')} <Timer finishEvent={item?.nextStatusAt} format="mm:ss" />
              </span>
            </span>
            <i className="icon-redirect-link" />
          </Button>
        ) : (
          <Button
            className={`primary with-progress ${cn} ${!item.order?.buyerTradeLink && 'disabled'}`}
            as="a"
            target="_blank"
            href={item.order?.buyerTradeLink}
            duration={+(Date.parse(item?.nextStatusAt) - Date.now()).toFixed(0)}
            current={dayjs(item?.statusAt).diff(Date.now())}
            finished={dayjs(item?.statusAt).diff(item?.nextStatusAt)}
          >
            <i className="icon-steam-provider" />
            <span className="multi-line">
              <span>{t('Send trade in Steam')}</span>
              <span>
                {t('Trade timeout')} <Timer finishEvent={item?.nextStatusAt} format="mm:ss" />
              </span>
            </span>
            <i className="icon-redirect-link" />
          </Button>
        )}
      </div>
    </Container>
  );
};
